import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiService } from '../service/Api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};   

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isInExtension, setIsInExtension] = useState(false);


  useEffect(() => {
    // Don't check auth status if we're on the callback route - let CallBack component handle it
    const isCallbackRoute = window.location.pathname === '/callback';
    
    if (!isCallbackRoute) {
      checkAuthStatus();
    } else {
      // On callback route, wait a bit to let the CallBack component process URL params first
      setLoading(true);
    }

    const inIframe = window.self !== window.top;
   
    setIsInExtension(inIframe);
    
    if (inIframe) {
      // Listen for messages from extension
      window.addEventListener('message', handleExtensionMessage);
      
      // Notify extension that frontend is ready
      window.parent.postMessage({ type: 'AURA_READY' }, '*');
    }
    
    return () => {
      if (inIframe) {
        window.removeEventListener('message', handleExtensionMessage);
      }
    };
  }, []);


  const checkAuthStatus = async (skipVerify = false) => {
    try {
      console.log("checking auth status", skipVerify ? "(skip verify)" : "");
      const token = localStorage.getItem('aura_token');
      const tenantId = localStorage.getItem('aura_tenant_id');
      
      if (!token) {
        setLoading(false);
        return;
      }
     
      apiService.setAuthToken(token);
      
      // Restore tenant_id if available
      if (tenantId) {
        apiService.setTenantId(tenantId);
      }

      // Skip verification if auth was just restored from extension
      if (skipVerify) {
        setLoading(false);
        return;
      }

      const response = await apiService.verifyAuth();
      
      if (response.data.valid) {
        setUser(response.data.user);
      } else {
        localStorage.removeItem('aura_token');
        localStorage.removeItem('aura_tenant_id');
        apiService.setAuthToken(null);
        apiService.setTenantId(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('aura_token');
      localStorage.removeItem('aura_tenant_id');
      apiService.setAuthToken(null);
      apiService.setTenantId(null);
    } finally {
      setLoading(false);
    }
  };

 
  const handleExtensionMessage = async (event) => {
    if (event.data.type === 'RESTORE_AUTH') {
      const { auth, accountDomain, tenantId } = event.data;
      if (auth && auth.token) {
        localStorage.setItem('aura_token', auth.token);
        apiService.setAuthToken(auth.token);
        setUser(auth.user);
        
        // Restore tenant_id if provided
        if (tenantId) {
          localStorage.setItem('aura_tenant_id', tenantId);
          apiService.setTenantId(tenantId);
          console.log('Extension tenant_id restored:', tenantId);
        }
        
        // Mark as loaded to avoid unnecessary verify call
        setLoading(false);
      }
    } else if (event.data.type === 'EXTENSION_AUTH_TOKEN') {
      // Handle auth token and tenant_id from OAuth completed in new tab
      const token = event.data.token;
      const tenantId = event.data.tenantId;
      console.log('Processing auth token from extension:', token);
      console.log('Processing tenant_id from extension:', tenantId);
      
      try {
        // Set token and verify auth
        localStorage.setItem('aura_token', token);
        apiService.setAuthToken(token);
        
        // Set tenant_id if provided
        if (tenantId) {
          localStorage.setItem('aura_tenant_id', tenantId);
          apiService.setTenantId(tenantId);
          console.log('Extension tenant_id stored and set:', tenantId);
        }
        
        const userResponse = await apiService.verifyAuth();
        
        if (userResponse.data.valid) {
          const userData = userResponse.data.user;
          setUser(userData);
          
          // Extract account domain
          const accountDomain = userData.email ? 
            userData.email.split('@')[1] : 
            userData.accountDomain || '';
          
          // Notify extension of successful auth (include tenant_id)
          notifyExtensionAuth(token, userData, accountDomain, tenantId);
          
          console.log('Extension auth successful, user state set');
        }
      } catch (error) {
        console.error('Error processing extension auth token:', error);
      }
    }
  };

  // Notify extension when user logs in
  const notifyExtensionAuth = (token, userData, accountDomain, tenantId = null) => {
    if (isInExtension && window.parent) {
      const message = {
        type: 'AURA_AUTH_SUCCESS',
        auth: { token, user: userData },
        accountDomain: accountDomain
      };
      
      // Include tenant_id if provided
      if (tenantId) {
        message.tenantId = tenantId;
      }
      
      window.parent.postMessage(message, '*');
    }
  };



  const login = (token, userData = null) => {
    localStorage.setItem('aura_token', token);
    apiService.setAuthToken(token);
    
    // If user data is provided, set it directly to avoid unnecessary API call
    if (userData) {
      setUser(userData);
      setLoading(false);
    } else {
      checkAuthStatus();
    }
  };

  const logout = async () => {
    try {
      // Check if there's an admin session backup to restore
      const adminTokenBackup = localStorage.getItem('aura_admin_token_backup');
      const adminTenantIdBackup = localStorage.getItem('aura_admin_tenant_id_backup');
      
      // Clear current session
      localStorage.removeItem('aura_token');
      localStorage.removeItem('aura_tenant_id');
      apiService.setAuthToken(null);
      apiService.setTenantId(null);
      
      // If admin credentials exist, restore them
      if (adminTokenBackup && adminTenantIdBackup) {
        console.log('Restoring admin session after proxy logout');
        localStorage.setItem('aura_token', adminTokenBackup);
        localStorage.setItem('aura_tenant_id', adminTenantIdBackup);
        apiService.setAuthToken(adminTokenBackup);
        apiService.setTenantId(adminTenantIdBackup);
        
        // Remove backup keys
        localStorage.removeItem('aura_admin_token_backup');
        localStorage.removeItem('aura_admin_tenant_id_backup');
        
        // Verify and restore user data
        try {
          const response = await apiService.verifyAuth();
          if (response.data.valid) {
            setUser(response.data.user);
            setLoading(false);
            console.log('Admin session restored successfully');
            return true; // Return true to indicate admin session was restored
          }
        } catch (verifyError) {
          console.error('Error verifying restored admin session:', verifyError);
          // If verification fails, continue with normal logout
        }
      }
      
      // Normal logout - clear user
      setUser(null);
      return false; // Return false to indicate normal logout
    } catch (error) {
      console.error('Logout error:', error);
      setUser(null);
      return false;
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
    checkAuthStatus,
    isInExtension,
    notifyExtensionAuth,
    isAppInstalled: user?.isAppInstalled || false
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};